import React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

import Button, { Appearance } from "../../components/Button";
import Heading from "../../components/Heading";
import Modal from "../../components/Modal";
import Text from "../../components/Text";
import { DEV_STORE_ID } from "../../environment";
import { colors, fonts } from "../../theme";

const RECENT_VISIT_STORES = gql`
  query RecentVisitStores {
    visits(first: 20) {
      edges {
        node {
          id
          store {
            id
            name
          }
        }
      }
    }
  }
`;

const extractStoreId = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(/\/visits\/new(?:-unscheduled)?\/([^/?#]+)/i);
  const candidate = match?.[1] || trimmed;

  try {
    return decodeURIComponent(candidate);
  } catch {
    return candidate;
  }
};

interface Props {
  isOpen: boolean;
  navigation: any;
  onClose: () => void;
}

const StartVisitDialog: React.FC<Props> = ({ isOpen, navigation, onClose }) => {
  const [shopLink, setShopLink] = React.useState("");
  const [linkError, setLinkError] = React.useState("");
  const { data } = useQuery(RECENT_VISIT_STORES, {
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const recentStores = React.useMemo(() => {
    const stores = (data?.visits?.edges || []).map(
      (edge: any) => edge.node.store,
    );

    return stores.filter(
      (store: any, index: number) =>
        stores.findIndex((candidate: any) => candidate.id === store.id) ===
        index,
    );
  }, [data]);

  const startForStore = (storeId: string) => {
    onClose();
    navigation.navigate("NewVisitFlow", { storeId });
  };

  const startFromLink = () => {
    const storeId = extractStoreId(shopLink);

    if (!storeId || storeId.includes("/") || storeId.includes(" ")) {
      setLinkError("Enter the shop link or code provided by the shop.");
      return;
    }

    setLinkError("");
    startForStore(storeId);
  };

  return (
    <Modal
      isVisible={isOpen}
      onBackdropPress={onClose}
      onBackButtonPress={onClose}
      style={styles.modal}
    >
      <View style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerCopy}>
            <Heading size="l" style={styles.title}>
              Start a new visit
            </Heading>
            <Text style={styles.subtitle}>
              Choose a recent shop or use the link provided by the shop.
            </Text>
          </View>
          <TouchableOpacity
            accessibilityLabel="Close"
            accessibilityRole="button"
            onPress={onClose}
            style={styles.closeButton}
          >
            <Text variant="bold" style={styles.closeText}>
              X
            </Text>
          </TouchableOpacity>
        </View>

        <ScrollView keyboardShouldPersistTaps="handled">
          {recentStores.length > 0 && (
            <View style={styles.section}>
              <Text variant="bold" style={styles.label}>
                Recent shops
              </Text>
              {recentStores.map((store: any) => (
                <Button
                  key={store.id}
                  title={store.name}
                  appearance={Appearance.ghost}
                  onPress={() => startForStore(store.id)}
                  style={styles.shopButton}
                />
              ))}
            </View>
          )}

          {__DEV__ &&
            !recentStores.some((store: any) => store.id === DEV_STORE_ID) && (
              <View style={styles.section}>
                <Text variant="bold" style={styles.label}>
                  Local shop
                </Text>
                <Button
                  title="House of Tattoo"
                  appearance={Appearance.ghost}
                  onPress={() => startForStore(DEV_STORE_ID)}
                />
              </View>
            )}

          <View style={styles.section}>
            <Text variant="bold" style={styles.label}>
              Shop link or code
            </Text>
            <TextInput
              accessibilityLabel="Shop link or code"
              autoCapitalize="none"
              autoCorrect={false}
              onChangeText={(value) => {
                setShopLink(value);
                setLinkError("");
              }}
              onSubmitEditing={startFromLink}
              placeholder="https://.../visits/new/..."
              placeholderTextColor={colors.darkGrey}
              style={[styles.linkInput, !!linkError && styles.linkInputError]}
              value={shopLink}
            />
            {!!linkError && (
              <Text style={styles.errorMessage}>{linkError}</Text>
            )}
            <Button
              title="Continue to shop"
              disabled={!shopLink.trim()}
              onPress={startFromLink}
            />
          </View>

          <Text size="s" style={styles.qrHelp}>
            You can also scan the QR code supplied by the shop with your phone
            camera. Shop availability is checked before a new visit begins.
          </Text>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    alignItems: "center",
    justifyContent: "center",
    margin: 20,
  },
  card: {
    backgroundColor: colors.black,
    borderColor: colors.darkestGrey,
    borderRadius: 16,
    borderWidth: 1,
    maxHeight: "90%",
    maxWidth: 560,
    padding: 22,
    width: "100%",
  },
  header: {
    borderBottomColor: colors.darkestGrey,
    borderBottomWidth: 1,
    flexDirection: "row",
    marginBottom: 18,
    paddingBottom: 18,
  },
  headerCopy: {
    flex: 1,
  },
  title: {
    color: colors.white,
  },
  subtitle: {
    color: colors.midGrey,
    marginTop: 6,
  },
  closeButton: {
    alignItems: "center",
    height: 36,
    justifyContent: "center",
    marginLeft: 12,
    width: 36,
  },
  closeText: {
    color: colors.yellow,
    fontSize: 18,
  },
  section: {
    marginBottom: 20,
  },
  label: {
    color: colors.white,
    fontFamily: fonts.medium,
    marginBottom: 10,
  },
  shopButton: {
    marginBottom: 8,
  },
  linkInput: {
    backgroundColor: colors.white,
    borderColor: "transparent",
    borderRadius: 7,
    borderWidth: 1,
    color: colors.black,
    fontFamily: fonts.regular,
    fontSize: 15,
    marginBottom: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  linkInputError: {
    borderColor: colors.red,
    marginBottom: 4,
  },
  errorMessage: {
    color: colors.red,
    fontSize: 12,
    marginBottom: 10,
  },
  qrHelp: {
    color: colors.midGrey,
    lineHeight: 18,
    textAlign: "center",
  },
});

export default StartVisitDialog;
